import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Edit, Plus, Save, Trash2, X } from 'lucide-react';
import { toast } from '../../hooks/useToast';
import { getErrorMessage } from '../../lib/api';
import { opportunityService } from '../../services/opportunity.service';
import { productService } from '../../services/product.service';
import type {
  Opportunity,
  OpportunityInput,
  OpportunityProduct,
  OpportunityProductInput,
  OpportunityStage,
  Product,
} from '../../types/crm';
import Loading from '../../components/Loading';
import ActivityList from '../../components/crm/ActivityList';

const STAGES: OpportunityStage[] = ['planning', 'in_process', 'won', 'lost'];

const editOpportunitySchema = z.object({
  title: z.string().min(1, 'Title is required'),
  stage: z.enum(['planning', 'in_process', 'won', 'lost']),
  annual_cases: z.string().optional(),
  expected_close_date: z.string().optional(),
  notes: z.string().optional(),
});

type EditOpportunityFormData = z.infer<typeof editOpportunitySchema>;

const titleize = (value: string): string =>
  value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const stageBadge = (stage: OpportunityStage): string => {
  switch (stage) {
    case 'won':
      return 'bg-green-100 text-green-800';
    case 'in_process':
      return 'bg-blue-100 text-blue-800';
    case 'lost':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-yellow-100 text-yellow-800';
  }
};

export const OpportunityDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const opportunityId = id ? parseInt(id, 10) : NaN;

  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Product line item modal
  const [showProductModal, setShowProductModal] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [productForm, setProductForm] = useState({ product_id: '', quantity: '1', unit_price: '' });
  const [isAddingProduct, setIsAddingProduct] = useState(false);

  // Client modal
  const [showClientModal, setShowClientModal] = useState(false);
  const [clientForm, setClientForm] = useState({ client_name: '', sort_order: '0' });
  const [isAddingClient, setIsAddingClient] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditOpportunityFormData>({
    resolver: zodResolver(editOpportunitySchema),
  });

  const loadOpportunity = useCallback(async () => {
    setIsLoading(true);
    try {
      const opp = await opportunityService.getById(opportunityId);
      setOpportunity(opp);
      reset({
        title: opp.title,
        stage: opp.stage,
        annual_cases: opp.annual_cases ?? '',
        expected_close_date: opp.expected_close_date ?? '',
        notes: opp.notes ?? '',
      });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [opportunityId, reset]);

  useEffect(() => {
    if (Number.isNaN(opportunityId)) return;
    loadOpportunity();
  }, [opportunityId, loadOpportunity]);

  const onSave = async (data: EditOpportunityFormData) => {
    setIsSaving(true);
    try {
      const input: Partial<OpportunityInput> = {
        title: data.title,
        stage: data.stage,
        annual_cases: data.annual_cases?.trim() ? data.annual_cases.trim() : null,
        expected_close_date: data.expected_close_date?.trim() ? data.expected_close_date.trim() : null,
        notes: data.notes?.trim() ? data.notes.trim() : null,
      };
      const updated = await opportunityService.update(opportunityId, input);
      setOpportunity(updated);
      setIsEditing(false);
      toast.success('Opportunity updated');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const onDelete = async () => {
    if (!confirm('Delete this opportunity? This will also delete its line items and named clients.')) return;
    try {
      await opportunityService.delete(opportunityId);
      toast.success('Opportunity deleted');
      navigate('/crm/opportunities');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // ----- Product line items -----

  const openProductModal = async () => {
    setProductForm({ product_id: '', quantity: '1', unit_price: '' });
    setShowProductModal(true);
    try {
      const result = await productService.getAll({ per_page: 200, 'q[active_eq]': true });
      setProducts(result.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const onAddProduct = async () => {
    if (!productForm.product_id || !productForm.unit_price) {
      toast.error('Pick a product and enter a unit price');
      return;
    }
    setIsAddingProduct(true);
    try {
      const input: OpportunityProductInput = {
        product_id: parseInt(productForm.product_id, 10),
        quantity: productForm.quantity || '1',
        unit_price: productForm.unit_price,
      };
      await opportunityService.addProduct(opportunityId, input);
      toast.success('Product added');
      setShowProductModal(false);
      loadOpportunity();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsAddingProduct(false);
    }
  };

  const onUpdateLineItem = async (
    line: OpportunityProduct,
    patch: Partial<OpportunityProductInput>,
  ) => {
    try {
      await opportunityService.updateProduct(opportunityId, line.id, patch);
      loadOpportunity();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const onRemoveLineItem = async (line: OpportunityProduct) => {
    if (!confirm(`Remove ${line.product.name} from this opportunity?`)) return;
    try {
      await opportunityService.removeProduct(opportunityId, line.id);
      toast.success('Product removed');
      loadOpportunity();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // ----- Named clients -----

  const openClientModal = () => {
    const nextSort = opportunity ? opportunity.clients.length : 0;
    setClientForm({ client_name: '', sort_order: String(nextSort) });
    setShowClientModal(true);
  };

  const onAddClient = async () => {
    if (!clientForm.client_name.trim()) {
      toast.error('Client name is required');
      return;
    }
    setIsAddingClient(true);
    try {
      await opportunityService.addClient(opportunityId, {
        client_name: clientForm.client_name.trim(),
        sort_order: parseInt(clientForm.sort_order || '0', 10),
      });
      toast.success('Client added');
      setShowClientModal(false);
      loadOpportunity();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsAddingClient(false);
    }
  };

  const onRemoveClient = async (clientId: number) => {
    if (!confirm('Remove this client?')) return;
    try {
      await opportunityService.removeClient(opportunityId, clientId);
      toast.success('Client removed');
      loadOpportunity();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  if (isLoading) return <Loading />;
  if (!opportunity) return <div className="text-gray-500">Opportunity not found.</div>;

  const totalValue = opportunity.products.reduce((sum, line) => {
    const q = parseFloat(line.quantity || '0');
    const p = parseFloat(line.unit_price || '0');
    return sum + (Number.isFinite(q) && Number.isFinite(p) ? q * p : 0);
  }, 0);

  return (
    <div className="space-y-6">
      <Link to="/crm/opportunities" className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800">
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to opportunities
      </Link>

      <div className="card p-6">
        {isEditing ? (
          <form onSubmit={handleSubmit(onSave)} className="space-y-4">
            <div>
              <label htmlFor="title" className="label">Title</label>
              <input {...register('title')} id="title" type="text" className="input" />
              {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="stage" className="label">Stage</label>
                <select {...register('stage')} id="stage" className="input">
                  {STAGES.map((s) => (
                    <option key={s} value={s}>{titleize(s)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="annual_cases" className="label">Annual cases</label>
                <input {...register('annual_cases')} id="annual_cases" type="text" inputMode="decimal" className="input" />
              </div>
            </div>

            <div>
              <label htmlFor="expected_close_date" className="label">Expected close date</label>
              <input {...register('expected_close_date')} id="expected_close_date" type="date" className="input" />
            </div>

            <div>
              <label htmlFor="notes" className="label">Notes</label>
              <textarea {...register('notes')} id="notes" rows={3} className="input" />
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <button type="button" onClick={() => { setIsEditing(false); loadOpportunity(); }} className="btn btn-secondary">
                <X className="h-4 w-4 inline mr-1" />
                Cancel
              </button>
              <button type="submit" disabled={isSaving} className="btn btn-primary">
                <Save className="h-4 w-4 inline mr-1" />
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{opportunity.title}</h1>
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <Link to={`/crm/operators/${opportunity.operator.id}`} className="text-blue-600 hover:text-blue-800">
                    {opportunity.operator.name}
                  </Link>
                  <span className="text-gray-300">·</span>
                  <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${stageBadge(opportunity.stage)}`}>
                    {titleize(opportunity.stage)}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setIsEditing(true)} className="btn btn-secondary">
                  <Edit className="h-4 w-4 inline mr-1" />
                  Edit
                </button>
                <button onClick={onDelete} className="btn btn-secondary text-red-600">
                  <Trash2 className="h-4 w-4 inline mr-1" />
                  Delete
                </button>
              </div>
            </div>

            <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div>
                <dt className="text-gray-500">Contact</dt>
                <dd className="text-gray-900">{opportunity.contact?.full_name || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Expected close</dt>
                <dd className="text-gray-900">{opportunity.expected_close_date || '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Annual cases</dt>
                <dd className="text-gray-900">{opportunity.annual_cases || '—'}</dd>
              </div>
              {opportunity.notes && (
                <div className="sm:col-span-2">
                  <dt className="text-gray-500">Notes</dt>
                  <dd className="text-gray-900 whitespace-pre-wrap">{opportunity.notes}</dd>
                </div>
              )}
            </dl>
          </>
        )}
      </div>

      {/* Products section */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Products</h3>
          <button onClick={openProductModal} className="btn btn-primary btn-sm">
            <Plus className="h-4 w-4 inline mr-1" />
            Add product
          </button>
        </div>

        {opportunity.products.length === 0 ? (
          <div className="text-center text-gray-500 py-6">No products added yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                  <th className="px-4 py-2 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                  <th className="px-4 py-2 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase">Unit price</th>
                  <th className="px-4 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                  <th className="px-4 py-2 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">Loss reason</th>
                  <th className="px-4 py-2 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase">Line total</th>
                  <th className="px-4 py-2 bg-gray-50"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {opportunity.products.map((line) => {
                  const q = parseFloat(line.quantity || '0');
                  const p = parseFloat(line.unit_price || '0');
                  const lineTotal = Number.isFinite(q) && Number.isFinite(p) ? q * p : 0;
                  return (
                    <tr key={line.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{line.product.name}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600 text-right">
                        <input
                          type="text"
                          inputMode="decimal"
                          defaultValue={line.quantity || ''}
                          onBlur={(e) => {
                            if (e.target.value !== (line.quantity || '')) {
                              onUpdateLineItem(line, { quantity: e.target.value });
                            }
                          }}
                          className="input text-right w-24"
                        />
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600 text-right">
                        <input
                          type="text"
                          inputMode="decimal"
                          defaultValue={line.unit_price || ''}
                          onBlur={(e) => {
                            if (e.target.value !== (line.unit_price || '')) {
                              onUpdateLineItem(line, { unit_price: e.target.value });
                            }
                          }}
                          className="input text-right w-24"
                        />
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                        <select
                          value={line.stage}
                          onChange={(e) => onUpdateLineItem(line, { stage: e.target.value as OpportunityStage })}
                          className="input"
                        >
                          {STAGES.map((s) => (
                            <option key={s} value={s}>{titleize(s)}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                        <input
                          type="text"
                          defaultValue={line.loss_reason || ''}
                          onBlur={(e) => {
                            if (e.target.value !== (line.loss_reason || '')) {
                              onUpdateLineItem(line, { loss_reason: e.target.value || null });
                            }
                          }}
                          className="input"
                          placeholder="—"
                        />
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600 text-right">
                        {lineTotal.toFixed(2)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-right">
                        <button
                          onClick={() => onRemoveLineItem(line)}
                          className="text-red-600 hover:text-red-900"
                          aria-label={`Remove ${line.product.name}`}
                        >
                          <Trash2 className="h-4 w-4 inline" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-gray-50">
                  <td colSpan={5} className="px-4 py-2 text-right text-sm font-medium text-gray-700">Total estimated value</td>
                  <td className="px-4 py-2 text-right text-sm font-semibold text-gray-900">{totalValue.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Clients section */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Named clients</h3>
          <button onClick={openClientModal} className="btn btn-primary btn-sm">
            <Plus className="h-4 w-4 inline mr-1" />
            Add client
          </button>
        </div>

        {opportunity.clients.length === 0 ? (
          <div className="text-center text-gray-500 py-6">No clients added yet.</div>
        ) : (
          <ol className="space-y-2">
            {opportunity.clients.map((c) => (
              <li
                key={c.id}
                className="flex items-center justify-between border border-gray-200 rounded px-3 py-2"
              >
                <span className="text-sm text-gray-900">
                  <span className="text-gray-400 mr-2">{c.sort_order}</span>
                  {c.client_name}
                </span>
                <button
                  onClick={() => onRemoveClient(c.id)}
                  className="text-red-600 hover:text-red-900"
                  aria-label={`Remove ${c.client_name}`}
                >
                  <Trash2 className="h-4 w-4 inline" />
                </button>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* Activity section */}
      <div className="card p-6">
        <ActivityList opportunityId={opportunity.id} defaultOperatorId={opportunity.operator.id} />
      </div>

      {/* Add product modal */}
      {showProductModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowProductModal(false)} />
            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Add product line item</h3>
                  <button type="button" onClick={() => setShowProductModal(false)} className="text-gray-400 hover:text-gray-500">
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="line_product" className="label">Product</label>
                    <select
                      id="line_product"
                      className="input"
                      value={productForm.product_id}
                      onChange={(e) => {
                        const productId = e.target.value;
                        const selected = products.find((p) => String(p.id) === productId);
                        // Prefill the unit price from the catalog list_price, but
                        // only when the user hasn't already typed a custom price.
                        const nextUnitPrice = productForm.unit_price
                          ? productForm.unit_price
                          : selected?.list_price ?? '';
                        setProductForm({ ...productForm, product_id: productId, unit_price: nextUnitPrice });
                      }}
                    >
                      <option value="">Select a product…</option>
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="line_quantity" className="label">Quantity</label>
                      <input
                        id="line_quantity"
                        type="text"
                        inputMode="decimal"
                        className="input"
                        value={productForm.quantity}
                        onChange={(e) => setProductForm({ ...productForm, quantity: e.target.value })}
                      />
                    </div>
                    <div>
                      <label htmlFor="line_unit_price" className="label">Unit price</label>
                      <input
                        id="line_unit_price"
                        type="text"
                        inputMode="decimal"
                        className="input"
                        value={productForm.unit_price}
                        onChange={(e) => setProductForm({ ...productForm, unit_price: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                <button type="button" onClick={onAddProduct} disabled={isAddingProduct} className="btn btn-primary">
                  {isAddingProduct ? 'Adding...' : 'Add Product'}
                </button>
                <button type="button" onClick={() => setShowProductModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add client modal */}
      {showClientModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowClientModal(false)} />
            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Add named client</h3>
                  <button type="button" onClick={() => setShowClientModal(false)} className="text-gray-400 hover:text-gray-500">
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="client_name" className="label">Client name</label>
                    <input
                      id="client_name"
                      type="text"
                      className="input"
                      value={clientForm.client_name}
                      onChange={(e) => setClientForm({ ...clientForm, client_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label htmlFor="sort_order" className="label">Sort order</label>
                    <input
                      id="sort_order"
                      type="number"
                      className="input"
                      value={clientForm.sort_order}
                      onChange={(e) => setClientForm({ ...clientForm, sort_order: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                <button type="button" onClick={onAddClient} disabled={isAddingClient} className="btn btn-primary">
                  {isAddingClient ? 'Adding...' : 'Add Client'}
                </button>
                <button type="button" onClick={() => setShowClientModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpportunityDetail;
